import React, { useState, useEffect } from "react";
import { X, Mail, BookOpen } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const STORAGE_KEY = "dc_newsletter_dismissed_v1";
const SUBSCRIBED_KEY = "dc_newsletter_subscribed_v1";
const DELAY_MS = 22000; // appear after 22 seconds

export default function EmailCapturePopup() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(SUBSCRIBED_KEY) === "true") return;
    if (localStorage.getItem(STORAGE_KEY) === "true") return;

    const onScroll = () => {
      // Trigger when user scrolls 60% of page
      const scrolled = (window.scrollY + window.innerHeight) / document.body.scrollHeight;
      if (scrolled > 0.6) {
        setOpen(true);
        window.removeEventListener("scroll", onScroll);
        clearTimeout(timer);
      }
    };
    const timer = setTimeout(() => {
      setOpen(true);
      window.removeEventListener("scroll", onScroll);
    }, DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, "true");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter/signup", { email, source: "popup" });
      localStorage.setItem(SUBSCRIBED_KEY, "true");
      setDone(true);
      if (data.already_subscribed) {
        toast.info("You're already on the list — thanks!");
      } else {
        toast.success("You're in! Welcome to the apothecary.");
      }
      setTimeout(() => setOpen(false), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || pathname.startsWith("/admin") || user?.role === "admin") return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6 bg-[var(--ink-forest)]/40 backdrop-blur-sm animate-in fade-in duration-300" data-testid="email-popup">
      <div className="bg-[var(--bg-surface)] rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-[var(--border)] relative animate-in slide-in-from-bottom-4 duration-500">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 p-2 hover:bg-[var(--muted)] rounded-full z-10"
          aria-label="Close"
          data-testid="email-popup-close"
        >
          <X className="w-4 h-4 text-[var(--ink-forest)]" />
        </button>

        <div className="grid sm:grid-cols-5">
          <div className="sm:col-span-2 bg-[var(--ink-forest)] text-[var(--bg-cream)] p-6 sm:p-8 flex items-center justify-center min-h-[140px]">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--gold)]/20 flex items-center justify-center mx-auto mb-3">
                <BookOpen className="w-6 h-6 text-[var(--gold)]" />
              </div>
              <p className="eyebrow text-[var(--gold)]">Free gift</p>
              <p className="font-serif text-xl mt-1 leading-tight">Starter Recipe Collection</p>
            </div>
          </div>

          <div className="sm:col-span-3 p-6 sm:p-8">
            {done ? (
              <div className="text-center py-4">
                <p className="font-serif text-2xl text-[var(--ink-forest)]">Welcome in. ✦</p>
                <p className="mt-2 text-sm text-[var(--muted-fg)]">
                  Check your inbox — your starter recipes are on their way.
                </p>
              </div>
            ) : (
              <>
                <p className="eyebrow mb-2">Loved from farm to bottle</p>
                <h3 className="font-serif text-2xl text-[var(--ink-forest)] leading-tight">
                  Get our free <span className="italic text-[var(--terracotta)]">starter</span> recipe collection.
                </h3>
                <p className="mt-2 text-sm text-[var(--muted-fg)] leading-relaxed">
                  Five hand-picked recipes — calming, cleaning, and cozy — delivered straight to your inbox. Save them for whenever you&apos;re ready to make.
                </p>

                <form onSubmit={submit} className="mt-5 space-y-3" data-testid="email-popup-form">
                  <div className="flex items-center gap-2 border border-[var(--border)] rounded-full px-4 py-3 bg-[var(--bg-cream)] focus-within:border-[var(--terracotta)]">
                    <Mail className="w-4 h-4 text-[var(--muted-fg)]" />
                    <input
                      type="email"
                      required
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm"
                      data-testid="email-popup-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[var(--terracotta)] text-white rounded-full py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors disabled:opacity-60"
                    data-testid="email-popup-submit"
                  >
                    {submitting ? "Sending…" : "Send me the recipes"}
                  </button>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="block mx-auto text-xs text-[var(--muted-fg)] hover:text-[var(--ink-forest)] underline underline-offset-2"
                  >
                    No thanks
                  </button>
                </form>

                <p className="mt-4 text-[10px] text-[var(--muted-fg)] text-center leading-relaxed">
                  We&apos;ll never share your email. Unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
